import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Stack,
  Grid,
  Container,
  ButtonGroup,
  Tooltip,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RegressionAccordion from '../../components/RegressionAccordion/RegressionAccordion';
import FundReturnInput from '../../components/FundReturnInput';
import BenchmarkInputs from '../../components/BenchmarkInputs';
import PageTitle from '../../components/PageTitle/PageTitle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CodeIcon from '@mui/icons-material/Code';
import DatasetIcon from '@mui/icons-material/Dataset';
import SaveIcon from '@mui/icons-material/Save';
import { API_BASE_URL } from '../../config/api';
import { normalizeReturnRows } from '../../utils/returnStreamRows';

// Example data imports
import DevelopedAllCapEquityData from './DevelopedAllCapEquityData.json';

const CUSTOM_REGRESSION_PRESETS_STORAGE_KEY = 'customRegressionInputPresets';
const BUILT_IN_PRESET_PREFIX = 'built-in:';
const CUSTOM_PRESET_PREFIX = 'custom:';

const regressionInputPresets = Object.freeze({
  developed: {
    label: 'Developed Markets',
    residual_return_streams: [
      { description: 'Equity', source: 'MSCI ACWI', residualization: [] },
      { description: 'EM', source: 'MSCI Emerging Markets', residualization: ['Equity'] },
      { description: 'Size', source: 'FF_DEV_5_SMB', residualization: [] },
      { description: 'Value', source: 'FF_DEV_5_HML', residualization: [] },
      { description: 'Quality', source: 'FF_DEV_5_RMW', residualization: [] },
      { description: 'Investment', source: 'FF_DEV_5_CMA', residualization: [] },
      { description: 'Momentum', source: 'FF_DEV_MOM', residualization: [] },
    ],
  },
  emerging: {
    label: 'Emerging Markets',
    residual_return_streams: [
      { description: 'Equity', source: 'MSCI Emerging Markets', residualization: [] },
      { description: 'Size', source: 'FF_EM_5_SMB', residualization: [] },
      { description: 'Value', source: 'FF_EM_5_HML', residualization: [] },
      { description: 'Quality', source: 'FF_EM_5_RMW', residualization: [] },
      { description: 'Investment', source: 'FF_EM_5_CMA', residualization: [] },
      { description: 'Momentum', source: 'FF_EM_MOM', residualization: [] },
    ],
  },
});

const cloneReturnStreams = (streams = []) => (
  streams.map((stream) => ({
    ...stream,
    residualization: [...(stream.residualization || [])],
  }))
);

const buildRegressionInputPresetData = (preset) => ({
  residual_return_streams: cloneReturnStreams(preset.residual_return_streams),
});

const developedRegressionInputDefaults = buildRegressionInputPresetData(regressionInputPresets.developed);

const initialData = {
  fund: { description: 'Input Fund Name', source: '', selectedEtf: null, pastedData: [] },
  benchmark: { description: 'MSCI ACWI Index', source: 'MSCI ACWI' },
  ...developedRegressionInputDefaults,
};

const base_url = API_BASE_URL;
const comparisonSearchProviders = Object.freeze({
  yahoo: true,
  msci: true,
  sp: true,
  iceBofa: true,
  famaFrench: true,
  ftseRussell: true,
});

const normalizeComparisonData = (payload = initialData) => ({
  ...payload,
  fund: {
    ...initialData.fund,
    ...(payload?.fund || {}),
    pastedData: normalizeReturnRows(payload?.fund?.pastedData || []),
  },
  benchmark: {
    ...initialData.benchmark,
    ...(payload?.benchmark || {}),
  },
  residual_return_streams: cloneReturnStreams(
    Array.isArray(payload?.residual_return_streams)
      ? payload.residual_return_streams
      : initialData.residual_return_streams,
  ),
});

const returnStreamMatchesPreset = (actualStream = {}, presetStream = {}) => (
  actualStream.description === presetStream.description
  && actualStream.source === presetStream.source
  && JSON.stringify(actualStream.residualization || []) === JSON.stringify(presetStream.residualization || [])
);

const returnStreamsMatchPreset = (streams = [], preset = {}) => (
  streams.length === preset.residual_return_streams.length
  && preset.residual_return_streams.every((presetStream, index) => (
    returnStreamMatchesPreset(streams[index], presetStream)
  ))
);

const getBuiltInPresetKey = (presetKey) => `${BUILT_IN_PRESET_PREFIX}${presetKey}`;
const getCustomPresetKey = (presetId) => `${CUSTOM_PRESET_PREFIX}${presetId}`;

const getMatchingRegressionInputPresetKey = (streams = [], customPresets = []) => {
  const builtInMatch = Object.entries(regressionInputPresets).find(([, preset]) => (
    returnStreamsMatchPreset(streams, preset)
  ));

  if (builtInMatch) {
    return getBuiltInPresetKey(builtInMatch[0]);
  }

  const customMatch = customPresets.find((preset) => returnStreamsMatchPreset(streams, preset));

  return customMatch ? getCustomPresetKey(customMatch.id) : '';
};

const loadSavedComparisonData = () => {
  if (typeof localStorage === 'undefined') {
    return initialData;
  }

  const savedData = localStorage.getItem('comparisonData');
  if (!savedData) {
    return initialData;
  }

  try {
    return normalizeComparisonData(JSON.parse(savedData));
  } catch {
    return initialData;
  }
};

const normalizeCustomRegressionPreset = (preset = {}) => {
  const name = String(preset.name || '').trim();
  const streams = Array.isArray(preset.residual_return_streams)
    ? cloneReturnStreams(preset.residual_return_streams)
    : [];

  if (!name || !streams.length) {
    return null;
  }

  return {
    id: String(preset.id || name.toLowerCase().replace(/[^a-z0-9]+/g, '-')).trim(),
    name,
    residual_return_streams: streams,
  };
};

const loadSavedCustomRegressionPresets = () => {
  if (typeof localStorage === 'undefined') {
    return [];
  }

  const savedPresets = localStorage.getItem(CUSTOM_REGRESSION_PRESETS_STORAGE_KEY);
  if (!savedPresets) {
    return [];
  }

  try {
    const parsedPresets = JSON.parse(savedPresets);
    return (Array.isArray(parsedPresets) ? parsedPresets : [])
      .map(normalizeCustomRegressionPreset)
      .filter(Boolean);
  } catch {
    return [];
  }
};

const getRegressionInputPresetByKey = (presetKey, customPresets = []) => {
  if (presetKey.startsWith(BUILT_IN_PRESET_PREFIX)) {
    const builtInKey = presetKey.slice(BUILT_IN_PRESET_PREFIX.length);
    return regressionInputPresets[builtInKey] || null;
  }

  if (presetKey.startsWith(CUSTOM_PRESET_PREFIX)) {
    const presetId = presetKey.slice(CUSTOM_PRESET_PREFIX.length);
    const customPreset = customPresets.find((preset) => preset.id === presetId);
    return customPreset
      ? {
          label: customPreset.name,
          residual_return_streams: customPreset.residual_return_streams,
        }
      : null;
  }

  return null;
};

const getRegressionInputPresetLabel = (presetKey, customPresets = []) => {
  if (!presetKey) {
    return 'Select preset';
  }

  if (presetKey.startsWith(BUILT_IN_PRESET_PREFIX)) {
    const builtInKey = presetKey.slice(BUILT_IN_PRESET_PREFIX.length);
    return regressionInputPresets[builtInKey]?.label || 'Select preset';
  }

  if (presetKey.startsWith(CUSTOM_PRESET_PREFIX)) {
    const presetId = presetKey.slice(CUSTOM_PRESET_PREFIX.length);
    return customPresets.find((preset) => preset.id === presetId)?.name || 'Select preset';
  }

  return 'Select preset';
};

const getInitialSelectedRegressionPresetKey = () => (
  getMatchingRegressionInputPresetKey(
    loadSavedComparisonData().residual_return_streams,
    loadSavedCustomRegressionPresets(),
  )
);

const SelectComparisons = () => {
  const navigate = useNavigate();

  // Track when example data is loaded
  const [isExampleDataLoaded, setIsExampleDataLoaded] = useState(false);
  const [exampleName, setExampleName] = useState('');

  const [data, setData] = useState(loadSavedComparisonData);
  const [customRegressionPresets, setCustomRegressionPresets] = useState(loadSavedCustomRegressionPresets);
  const [newRegressionPresetName, setNewRegressionPresetName] = useState('');
  const [savePresetDialogOpen, setSavePresetDialogOpen] = useState(false);
  const [selectedRegressionPresetKey, setSelectedRegressionPresetKey] = useState(
    getInitialSelectedRegressionPresetKey,
  );
  const [loading, setLoading] = useState(false);
  const [exampleLoading, setExampleLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  useEffect(() => {
    console.log('State updated:', data);
    localStorage.setItem('comparisonData', JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    localStorage.setItem(
      CUSTOM_REGRESSION_PRESETS_STORAGE_KEY,
      JSON.stringify(customRegressionPresets),
    );
  }, [customRegressionPresets]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const parseApiJson = async (response, contextLabel) => {
    const rawText = await response.text();
    const trimmedText = rawText.trim();

    try {
      return trimmedText ? JSON.parse(trimmedText) : {};
    } catch {
      const looksLikeHtml =
        trimmedText.toLowerCase().startsWith('<!doctype') ||
        trimmedText.toLowerCase().startsWith('<html');
      if (looksLikeHtml) {
        throw new Error(
          `${contextLabel} returned HTML instead of JSON. Check API base URL/proxy target and backend port.`,
        );
      }
      throw new Error(`${contextLabel} returned invalid JSON.`);
    }
  };

  const getFundStartDate = (fundRows = []) => {
    const parsedDates = fundRows
      .map((row) => new Date(row?.date))
      .filter((date) => !Number.isNaN(date.getTime()));

    if (!parsedDates.length) {
      return null;
    }

    const minDate = parsedDates.reduce((minSoFar, current) => (current < minSoFar ? current : minSoFar));
    return minDate.toISOString().slice(0, 10);
  };

  const buildReturnRequests = (payload) => {
    const requests = [];
    const addRequest = (source, type, description = '') => {
      const normalizedSource = (source || '').trim();
      if (!normalizedSource) {
        return;
      }
      requests.push({
        source: normalizedSource,
        type,
        description: (description || '').trim(),
      });
    };

    addRequest(payload?.benchmark?.source, 'benchmark', payload?.benchmark?.description);
    (payload?.residual_return_streams || []).forEach((stream) => {
      addRequest(stream?.source, 'residual_stream', stream?.description);
    });

    const seen = new Set();
    return requests.filter((item) => {
      const key = item.source.toLowerCase();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  const mapCumulativeToRows = (cumulativeReturns = []) => (
    normalizeReturnRows(
      (Array.isArray(cumulativeReturns) ? cumulativeReturns : [])
        .filter((entry) => entry?.date && entry?.return_rate != null)
        .map((entry, index) => ({
          id: index + 1,
          date: String(entry.date).trim(),
          return: String(entry.return_rate).trim(),
        })),
    )
  );

  const collectFundEtfRows = async (fundPayload = {}) => {
    const symbol = (fundPayload?.source || fundPayload?.selectedEtf?.symbol || '').trim();
    if (!symbol) {
      return null;
    }

    const startDate = getFundStartDate(fundPayload?.pastedData || []);
    const response = await fetch(`${base_url}/api/etf/collect-returns`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        symbol,
        name: fundPayload?.description || symbol,
        start_date: startDate,
      }),
    });

    const payload = await parseApiJson(response, 'Fund symbol collect API');
    if (!response.ok) {
      throw new Error(payload.error || `Fund symbol collection failed (${response.status})`);
    }

    const selectedEtf = {
      symbol: payload.symbol || symbol,
      name: payload.name || fundPayload?.description || symbol,
      start_date: payload.start_date || null,
      end_date: payload.end_date || null,
      cumulative_returns: Array.isArray(payload.cumulative_returns) ? payload.cumulative_returns : [],
    };

    const rows = mapCumulativeToRows(selectedEtf.cumulative_returns);
    if (!rows.length) {
      throw new Error(`No monthly returns were returned for ${selectedEtf.symbol}.`);
    }

    return { rows, selectedEtf };
  };

  const exampleFundHasMatchingBundledRows = (fundPayload = {}) => {
    const sourceSymbol = (fundPayload?.source || '').trim().toUpperCase();
    const selectedSymbol = (fundPayload?.selectedEtf?.symbol || '').trim().toUpperCase();
    const bundledRows = Array.isArray(fundPayload?.pastedData) ? fundPayload.pastedData : [];

    if (!sourceSymbol || !selectedSymbol || !bundledRows.length) {
      return false;
    }

    return sourceSymbol === selectedSymbol;
  };

  // -----------------------------
  // Handlers for Submit, Reset, etc.
  // -----------------------------
  const handleSubmit = async () => {
    // Validate data before submission
    if (data.fund.pastedData.length === 0) {
      setSnackbarSeverity('warning');
      setSnackbarMessage('Please paste fund returns above');
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    setSnackbarSeverity('info');
    setSnackbarMessage('This may take a few minutes...');
    setSnackbarOpen(true);

    const url = base_url + '/api/submit-return_attribution-data';
    const fundStartDate = getFundStartDate(data?.fund?.pastedData || []);
    const returnRequests = buildReturnRequests(data);
    const payload = {
      ...data,
      return_requests: returnRequests.map((requestItem) => ({
        ...requestItem,
        start_date: fundStartDate,
      })),
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseData = await parseApiJson(response, 'Submit API');
      if (!response.ok) {
        throw new Error(responseData.error || `Submit failed (${response.status})`);
      }

      const analysisId = responseData.analysis_id || responseData.task_id;
      if (!analysisId) {
        throw new Error('Submit API did not return an analysis id.');
      }

      localStorage.setItem('submittedData', JSON.stringify(data));
      localStorage.setItem('returnAttributionAnalysisId', analysisId);
      const basePath = window.location.pathname.startsWith('/insights') ? '/insights' : '';
      const targetPath = `${basePath}/manager-selection/tools/return-attribution/analysis`;

      setLoading(false);
      navigate(targetPath, {
        state: {
          analysisId,
          data: responseData.result || null,
        },
      });
    } catch (error) {
      console.error('Error:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage(`Error: ${error.message}`);
      setSnackbarOpen(true);
      setLoading(false);
    }
  };

  const handleReset = () => {
    setIsExampleDataLoaded(false);
    setExampleName('');
    setSelectedRegressionPresetKey(getBuiltInPresetKey('developed'));
    setNewRegressionPresetName('');
    setSavePresetDialogOpen(false);
    setData(normalizeComparisonData(initialData));
    localStorage.removeItem('comparisonData');
    localStorage.removeItem('returnAttributionAnalysisId');
    
    setSnackbarSeverity('info');
    setSnackbarMessage('All data has been reset');
    setSnackbarOpen(true);
  };

  const handleApplyRegressionInputPreset = (presetKey) => {
    setSelectedRegressionPresetKey(presetKey);

    if (!presetKey) {
      return;
    }

    const preset = getRegressionInputPresetByKey(presetKey, customRegressionPresets);
    if (!preset) {
      return;
    }

    setData((prevData) => ({
      ...prevData,
      ...buildRegressionInputPresetData(preset),
    }));

    setSnackbarSeverity('success');
    setSnackbarMessage(`${preset.label} regression inputs loaded`);
    setSnackbarOpen(true);
  };

  const handleOpenSaveRegressionPresetDialog = () => {
    const selectedCustomPreset = selectedRegressionPresetKey.startsWith(CUSTOM_PRESET_PREFIX)
      ? customRegressionPresets.find(
          (preset) => preset.id === selectedRegressionPresetKey.slice(CUSTOM_PRESET_PREFIX.length),
        )
      : null;

    setNewRegressionPresetName(selectedCustomPreset?.name || '');
    setSavePresetDialogOpen(true);
  };

  const handleCloseSaveRegressionPresetDialog = () => {
    setSavePresetDialogOpen(false);
  };

  const handleSaveCurrentRegressionPreset = () => {
    const name = newRegressionPresetName.trim();
    if (!name) {
      setSnackbarSeverity('warning');
      setSnackbarMessage('Enter a preset name before saving');
      setSnackbarOpen(true);
      return;
    }

    const existingPreset = customRegressionPresets.find(
      (preset) => preset.name.trim().toLowerCase() === name.toLowerCase(),
    );
    const presetToSave = {
      id: existingPreset?.id || `${Date.now()}`,
      name,
      residual_return_streams: cloneReturnStreams(data.residual_return_streams),
    };

    setCustomRegressionPresets((prevPresets) => {
      if (existingPreset) {
        return prevPresets.map((preset) => (
          preset.id === existingPreset.id ? presetToSave : preset
        ));
      }
      return [...prevPresets, presetToSave];
    });
    setSelectedRegressionPresetKey(getCustomPresetKey(presetToSave.id));
    setNewRegressionPresetName('');
    setSavePresetDialogOpen(false);

    setSnackbarSeverity('success');
    setSnackbarMessage(`${name} regression inputs ${existingPreset ? 'updated' : 'saved'}`);
    setSnackbarOpen(true);
  };

  const handleDeleteRegressionPreset = (presetId) => {
    const presetToDelete = customRegressionPresets.find((preset) => preset.id === presetId);
    if (!presetToDelete) {
      return;
    }

    setCustomRegressionPresets((prevPresets) => (
      prevPresets.filter((preset) => preset.id !== presetId)
    ));
    if (selectedRegressionPresetKey === getCustomPresetKey(presetId)) {
      setSelectedRegressionPresetKey('');
    }

    setSnackbarSeverity('info');
    setSnackbarMessage(`${presetToDelete.name} preset deleted`);
    setSnackbarOpen(true);
  };

  // -----------------------------
  // Handlers for data changes
  // -----------------------------
  const handleBenchmarkChange = (field, value) => {
    setData((prevData) => ({
      ...prevData,
      benchmark: { ...prevData.benchmark, [field]: value },
    }));
  };

  const handleDataChange = (index, field, value) => {
    setSelectedRegressionPresetKey('');
    setData((prevData) => {
      const updatedResiduals = prevData.residual_return_streams.map((residual, i) => {
        if (i === index) {
          return { ...residual, [field]: value };
        }
        if (field === 'description') {
          // Update any references to old description in other streams' residualization arrays
          return {
            ...residual,
            residualization: residual.residualization.map((residualItem) =>
              residualItem === prevData.residual_return_streams[index].description
                ? value
                : residualItem
            ),
          };
        }
        return residual;
      });
      return {
        ...prevData,
        residual_return_streams: updatedResiduals,
      };
    });
  };

  const handleRemoveRegression = (description) => {
    setSelectedRegressionPresetKey('');
    setData((prevData) => {
      const updatedResiduals = prevData.residual_return_streams
        .filter((residual) => residual.description !== description)
        .map((residual) => ({
          ...residual,
          residualization: residual.residualization.filter(
            (residualItem) => residualItem !== description
          ),
        }));
      return {
        ...prevData,
        residual_return_streams: updatedResiduals,
      };
    });
  };

  const handleAddResidual = () => {
    setSelectedRegressionPresetKey('');
    setData((prevData) => ({
      ...prevData,
      residual_return_streams: [
        ...prevData.residual_return_streams,
        {
          description: `New Residual ${prevData.residual_return_streams.length + 1}`,
          source: '',
          residualization: [],
        },
      ],
    }));
  };

  const handlePastedDataUpdate = (newData, selectedEtf = null) => {
    const normalizedRows = normalizeReturnRows(newData);

    console.log(normalizedRows, selectedEtf);
    setData((prevData) => ({
      ...prevData,
      fund: {
        ...prevData.fund,
        pastedData: normalizedRows,
        source: selectedEtf?.symbol || '',
        selectedEtf: selectedEtf || null,
        description: selectedEtf?.name || prevData.fund.description,
      },
    }));
  };

  // Modified: Function to load example data without auto-submitting
  const handleLoadExampleData = async (exampleData, name) => {
    setExampleLoading(true);
    const nextData = normalizeComparisonData(JSON.parse(JSON.stringify(exampleData)));

    try {
      if (nextData?.fund?.source) {
        const collected = await collectFundEtfRows(nextData.fund);
        if (collected) {
          nextData.fund = {
            ...nextData.fund,
            pastedData: collected.rows,
            source: collected.selectedEtf.symbol,
            selectedEtf: collected.selectedEtf,
            description: collected.selectedEtf.name || nextData.fund.description,
          };
        }
      }

      const normalizedNextData = normalizeComparisonData(nextData);
      setData(normalizedNextData);
      setSelectedRegressionPresetKey(
        getMatchingRegressionInputPresetKey(
          normalizedNextData.residual_return_streams,
          customRegressionPresets,
        ),
      );
      setIsExampleDataLoaded(true);
      setExampleName(name);

      setSnackbarSeverity('success');
      setSnackbarMessage(`Example data "${name}" loaded successfully. Click Submit to process.`);
      setSnackbarOpen(true);
      localStorage.setItem('comparisonData', JSON.stringify(nextData));
    } catch (error) {
      console.error('Error loading example fund symbol data:', error);

      const fallbackSelected = nextData?.fund?.source
        ? {
            symbol: nextData.fund.source,
            name: nextData.fund.description || nextData.fund.source,
            start_date: null,
            end_date: null,
            cumulative_returns: [],
          }
        : null;

      const fallbackData = normalizeComparisonData({
        ...nextData,
        fund: {
          ...nextData.fund,
          pastedData: exampleFundHasMatchingBundledRows(nextData.fund) ? nextData.fund.pastedData : [],
          selectedEtf: fallbackSelected,
        },
      });

      setData(fallbackData);
      setSelectedRegressionPresetKey(
        getMatchingRegressionInputPresetKey(
          fallbackData.residual_return_streams,
          customRegressionPresets,
        ),
      );
      setIsExampleDataLoaded(true);
      setExampleName(name);

      setSnackbarSeverity('warning');
      setSnackbarMessage(
        `Example "${name}" loaded, but live symbol refresh failed: ${error.message}`,
      );
      setSnackbarOpen(true);
      localStorage.setItem('comparisonData', JSON.stringify(fallbackData));
    } finally {
      setExampleLoading(false);
    }
  };

  const availableDescriptions = data.residual_return_streams.map((stream) => stream.description);

  return (
    <Container maxWidth="lg">
      <Box sx={{ padding: 2 }}>
        {/* Page Title */}
        <PageTitle title="Return Attribution" subtitle="Upload & Select Return Streams" />

        {/*
          ------------------------------------------------
          Action Buttons Moved to the Top
          ------------------------------------------------
        */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: 2,
            mt: 2,
            mb: 3,
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          {/* Left side - Example buttons with improved styling */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 1,
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                color: 'primary.main'
              }}
            >
              <DatasetIcon sx={{ mr: 1 }} />
              Example Datasets
              {exampleLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
            </Typography>
            <ButtonGroup
              variant="outlined"
              sx={{
                flexShrink: 0,
                '& .MuiButton-root': {
                  borderColor: 'primary.light',
                  backgroundColor: isExampleDataLoaded ? 'primary.50' : 'transparent',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.50',
                  },
                },
              }}
            >
              <Tooltip title="Try the example dataset with Berkshire Hathaway">
                <Button
                  startIcon={<CodeIcon />}
                  onClick={() => handleLoadExampleData(DevelopedAllCapEquityData, 'Berkshire Hathaway')}
                  disabled={exampleLoading}
                  sx={{
                    backgroundColor: exampleName === 'Berkshire Hathaway' ? 'rgba(0, 150, 255, 0.1)' : 'transparent',
                    fontWeight: exampleName === 'Berkshire Hathaway' ? 'bold' : 'normal',
                  }}
                >
                  Try with Berkshire Hathaway
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Box>

          {/* Right side - Action buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              flexShrink: 0,
              minWidth: { sm: 'auto' },
            }}
          >
            <Button
              variant="outlined"
              color="error"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              sx={{
                borderWidth: '1px',
                '&:hover': {
                  borderWidth: '1px',
                },
              }}
            >
              Reset Page
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={loading}
              onClick={handleSubmit}
              sx={{
                minWidth: 120,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 3,
                },
              }}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
            >
              {loading ? 'Processing' : 'Submit'}
            </Button>
          </Stack>
        </Box>

        {/* Display a message when example data is loaded */}
        {isExampleDataLoaded && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              mb: 3, 
              backgroundColor: 'rgba(0, 150, 255, 0.1)',
              border: '1px solid',
              borderColor: 'primary.light',
              borderRadius: 1
            }}
          >
            <Typography variant="body1" color="primary">
              <strong>Example Dataset Loaded:</strong> {exampleName}
            </Typography>
            <Typography variant="body2">
              Example data has been loaded successfully. Click the Submit button to process the analysis.
            </Typography>
          </Paper>
        )}

        <Grid container spacing={3}>
          {/* Fund Section */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Fund
              </Typography>
              <FundReturnInput
                fund={data.fund}
                updateFundReturns={handlePastedDataUpdate}
                pastedData={data.fund.pastedData}
                baseUrl={base_url}
                searchProviders={comparisonSearchProviders}
              />
            </Paper>
          </Grid>

          {/* Benchmark Section */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Benchmark
              </Typography>
              <BenchmarkInputs
                benchmarkData={data.benchmark}
                handleDescriptionChange={handleBenchmarkChange}
                handleStreamChange={handleBenchmarkChange}
                baseUrl={base_url}
                searchProviders={comparisonSearchProviders}
              />
            </Paper>
          </Grid>

          {/* Regression Return Streams Section */}
          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                  flexWrap: 'wrap',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Regression Return Streams</Typography>

                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ justifyContent: 'flex-end', alignItems: { xs: 'stretch', md: 'center' } }}
                >
                  <TextField
                    select
                    size="small"
                    label="Preset"
                    value={selectedRegressionPresetKey}
                    onChange={(event) => handleApplyRegressionInputPreset(event.target.value)}
                    sx={{ minWidth: { xs: '100%', md: 260 } }}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{
                      renderValue: (selected) => getRegressionInputPresetLabel(
                        selected,
                        customRegressionPresets,
                      ),
                    }}
                  >
                    <MenuItem value="">
                      Select preset
                    </MenuItem>
                    <MenuItem disabled value="built-in-heading">
                      Built-in presets
                    </MenuItem>
                    {Object.entries(regressionInputPresets).map(([presetKey, preset]) => (
                      <MenuItem key={presetKey} value={getBuiltInPresetKey(presetKey)}>
                        {preset.label}
                      </MenuItem>
                    ))}
                    {customRegressionPresets.length > 0 && (
                      <MenuItem disabled value="custom-heading">
                        Saved presets
                      </MenuItem>
                    )}
                    {customRegressionPresets.map((preset) => (
                      <MenuItem
                        key={preset.id}
                        value={getCustomPresetKey(preset.id)}
                        sx={{ pr: 1 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            gap: 2,
                          }}
                        >
                          <Typography component="span" variant="body2">
                            {preset.name}
                          </Typography>
                          <Tooltip title={`Delete ${preset.name}`}>
                            <IconButton
                              size="small"
                              aria-label={`Delete ${preset.name}`}
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleDeleteRegressionPreset(preset.id);
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    onClick={handleOpenSaveRegressionPresetDialog}
                    sx={{ minWidth: 92 }}
                  >
                    Save
                  </Button>
                </Stack>
              </Box>

              {data.residual_return_streams.map((stream, index) => (
                <RegressionAccordion
                  key={index}
                  index={index}
                  returnStream={stream}
                  handleDataChange={handleDataChange}
                  onRemove={() => handleRemoveRegression(stream.description)}
                  availableDescriptions={availableDescriptions}
                  data={data}
                  baseUrl={base_url}
                  searchProviders={comparisonSearchProviders}
                />
              ))}

              {/* Add Residual Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <IconButton
                  color="primary"
                  onClick={handleAddResidual}
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.primary.dark,
                    },
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Dialog
          open={savePresetDialogOpen}
          onClose={handleCloseSaveRegressionPresetDialog}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Save Regression Preset</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Preset name"
              value={newRegressionPresetName}
              onChange={(event) => setNewRegressionPresetName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleSaveCurrentRegressionPreset();
                }
              }}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSaveRegressionPresetDialog}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveCurrentRegressionPreset}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for Notifications */}
        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default SelectComparisons;
